import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { api, setAnalyticsEnabled, setAuthToken, setInstallId, track } from "@/lib/api";

const KEYS = {
  mode: "breez_mode",
  profile: "breez_profile",
  saved: "breez_saved",
  onboarded: "breez_onboarded",
  token: "auth_token",
  analytics: "breez_analytics_on",
  rememberScan: "breez_remember_scan",
};
const SCAN_FIELDS = ["face_shape", "hair_pattern", "length", "density", "strand_notes", "visible_hairstyle", "scanned_at"];

/** localStorage wrapper storing JSON values (same keys as the app). */
export const storage = {
  get(key, fallback = null) {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? fallback : JSON.parse(raw);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      if (value == null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage full / blocked */
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

const newInstallId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
const parse = (raw, fallback) => {
  if (!raw) return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const hasValues = (p) => Object.values(p ?? {}).some((v) => (Array.isArray(v) ? v.length : v && typeof v !== "object"));

const AppContext = createContext(null);
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
}

export function AppProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({});
  const [saved, setSaved] = useState([]);
  const [onboarded, setOnboarded] = useState(false);
  const [scanDraft, setScanDraft] = useState(null);
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const [rememberScan, setRememberScanState] = useState(true);
  const [dataEpoch, setDataEpoch] = useState(0);
  const [toastMsg, setToastMsg] = useState(null);
  const timer = useRef(null);
  const [recs, setRecs] = useState(null);
  const [routine, setRoutine] = useState(null);
  const [recsError, setRecsError] = useState(null);
  const [recsNonce, setRecsNonce] = useState(0);
  const [tryOn, setTryOn] = useState(null);
  const [scanPhoto, setScanPhoto] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const refreshBrand = useCallback(() => {
    api("/brand").then((b) => setBrandLogo(b.logo_url)).catch(() => {});
  }, []);
  const refreshRecs = useCallback(() => setRecsNonce((n) => n + 1), []);

  useEffect(() => {
    if (!ready) return;
    let alive = true;
    setRecsError(null);
    api("/recommendations", { method: "POST", body: { profile } })
      .then((r) => {
        if (!alive) return;
        setRecs(r.recommendations);
        setRoutine(r.routine);
      })
      .catch((e) => alive && setRecsError(e.message));
    return () => {
      alive = false;
    };
  }, [ready, profile, recsNonce]);

  const topSlugs = useMemo(
    () => new Set(profile.face_shape || profile.hair_pattern || profile.hair_type || profile.length ? (recs ?? []).map((r) => r.style.slug) : []),
    [recs, profile],
  );

  const toast = useCallback((msg, kind = "info") => {
    if (timer.current) clearTimeout(timer.current);
    setToastMsg({ msg, kind, id: Date.now() });
    timer.current = setTimeout(() => setToastMsg(null), 2800);
  }, []);

  const persistedRef = useRef({});
  const modeRef = useRef(null);
  const userRef = useRef(null);
  const rememberRef = useRef(true);
  modeRef.current = mode;
  userRef.current = user;
  rememberRef.current = rememberScan;

  const applyUser = useCallback((u) => {
    setUser(u);
    setProfile(u.profile ?? {});
    persistedRef.current = u.profile ?? {};
    setSaved(u.saved_styles ?? []);
  }, []);

  useEffect(() => {
    (async () => {
      let iid = storage.get("breez_install_id", null);
      if (typeof iid !== "string") {
        iid = newInstallId();
        storage.set("breez_install_id", iid);
      }
      const aOn = storage.get(KEYS.analytics, true) !== false;
      setAnalyticsOn(aOn);
      setAnalyticsEnabled(aOn);
      setRememberScanState(storage.get(KEYS.rememberScan, true) !== false);
      setInstallId(iid);
      track("app_open");
      refreshBrand();
      const m = storage.get(KEYS.mode, null);
      setOnboarded(Boolean(storage.get(KEYS.onboarded, false)));
      const lp = parse(storage.get(KEYS.profile, null), {});
      setProfile(lp);
      persistedRef.current = lp;
      setSaved(parse(storage.get(KEYS.saved, null), []));
      if (m === "user") {
        const token = storage.get(KEYS.token, null);
        if (typeof token === "string") {
          setAuthToken(token);
          try {
            applyUser(await api("/auth/me"));
            setMode("user");
          } catch {
            setAuthToken(null);
            storage.remove(KEYS.token);
            storage.set(KEYS.mode, null);
          }
        }
      } else if (m === "guest") {
        setMode("guest");
      }
      setReady(true);
    })();
  }, [applyUser, refreshBrand]);

  const continueAsGuest = useCallback(async () => {
    storage.set(KEYS.mode, "guest");
    setMode("guest");
  }, []);

  const finishAuth = useCallback(
    async (res) => {
      setAuthToken(res.token);
      storage.set(KEYS.token, res.token);
      storage.set(KEYS.mode, "user");
      let u = res.user;
      if (!hasValues(u.profile) && hasValues(profile)) {
        const p = await api("/me/profile", { method: "PUT", body: { profile } });
        u = { ...u, profile: p };
      }
      applyUser(u);
      setMode("user");
      return u;
    },
    [applyUser, profile],
  );

  const login = useCallback(
    async (email, password) => finishAuth(await api("/auth/login", { method: "POST", body: { email, password } })),
    [finishAuth],
  );

  const register = useCallback(
    async (name, email, password) =>
      finishAuth(await api("/auth/register", { method: "POST", body: { name, email, password, profile, saved_styles: saved } })),
    [finishAuth, profile, saved],
  );

  const logout = useCallback(async () => {
    setAuthToken(null);
    storage.remove(KEYS.token);
    storage.set(KEYS.mode, null);
    setUser(null);
    setMode(null);
    const lp = parse(storage.get(KEYS.profile, null), {});
    setProfile(lp);
    persistedRef.current = lp;
    setSaved(parse(storage.get(KEYS.saved, null), []));
  }, []);

  const saveProfile = useCallback(async (p) => {
    // "Remember my scan results" off → a scan save keeps the previously saved scan fields; new estimates stay in memory only.
    const scanSave = p.source === "scan" && !!p.scanned_at;
    let persist = p;
    if (!rememberRef.current && scanSave) {
      const prev = persistedRef.current;
      persist = { ...p, confidences: prev.confidences ?? {}, source: prev.source ?? null, hair_type: prev.hair_type ?? p.hair_type };
      SCAN_FIELDS.forEach((k) => (persist[k] = prev[k] ?? null));
    }
    if (modeRef.current === "user" || userRef.current) {
      const out = await api("/me/profile", { method: "PUT", body: { profile: persist } });
      persistedRef.current = out;
      setProfile(persist === p ? out : p);
    } else {
      storage.set(KEYS.profile, JSON.stringify(persist));
      persistedRef.current = persist;
      setProfile(p);
    }
  }, []);

  const setAnalytics = useCallback(async (on) => {
    if (on) {
      setAnalyticsEnabled(true);
      track("app_open");
    } else setAnalyticsEnabled(false);
    setAnalyticsOn(on);
    storage.set(KEYS.analytics, on);
  }, []);

  const setRememberScan = useCallback(async (on) => {
    setRememberScanState(on);
    storage.set(KEYS.rememberScan, on);
  }, []);

  /** Wipes everything this app keeps in this browser for the current person (guest or signed-in). */
  const clearDeviceData = useCallback(async () => {
    storage.remove(KEYS.profile);
    storage.remove(KEYS.saved);
    storage.remove("breez_cart_guest");
    if (user) storage.remove(`breez_cart_${user.id}`);
    setScanDraft(null);
    setScanPhoto(null);
    setTryOn(null);
    if (mode !== "user") {
      setProfile({});
      setSaved([]);
    }
    setDataEpoch((e) => e + 1);
  }, [mode, user]);

  const deleteAccount = useCallback(
    async (confirm) => {
      await api("/me/delete", { method: "POST", body: confirm });
      await clearDeviceData();
      const iid = newInstallId();
      storage.set("breez_install_id", iid);
      setInstallId(iid);
      setAuthToken(null);
      storage.remove(KEYS.token);
      storage.set(KEYS.mode, null);
      storage.set(KEYS.onboarded, false);
      setUser(null);
      setMode(null);
      setProfile({});
      setSaved([]);
      setOnboarded(false);
    },
    [clearDeviceData],
  );

  const toggleSaved = useCallback(
    async (slug) => {
      const isSaved = saved.includes(slug);
      if (mode === "user") {
        const res = await api(`/me/saved/${slug}`, { method: isSaved ? "DELETE" : "POST" });
        setSaved(res.slugs);
      } else {
        const next = isSaved ? saved.filter((s) => s !== slug) : [...saved, slug];
        storage.set(KEYS.saved, JSON.stringify(next));
        setSaved(next);
      }
      if (!isSaved) track("save", { slug });
      return !isSaved;
    },
    [mode, saved],
  );

  const completeOnboarding = useCallback(async () => {
    storage.set(KEYS.onboarded, true);
    setOnboarded(true);
  }, []);

  const clearScanHistory = useCallback(async () => {
    const base = persistedRef.current;
    const next = { ...base, confidences: {} };
    const fromScan = base.source === "scan" || !!base.scanned_at;
    (fromScan ? SCAN_FIELDS : ["strand_notes", "visible_hairstyle", "scanned_at"]).forEach((k) => {
      next[k] = null;
    });
    if (!fromScan) next.confidences = base.confidences ?? {};
    if (base.source === "scan") {
      next.hair_type = null;
      next.source = null;
    }
    await saveProfile(next);
    setScanDraft(null);
    setScanPhoto(null);
    setTryOn(null);
  }, [saveProfile]);

  const hasProfile = Boolean(profile.face_shape || profile.hair_pattern || profile.hair_type || profile.length);

  const value = useMemo(
    () => ({
      ready, mode, user, profile, saved, onboarded, hasProfile, scanDraft, setScanDraft,
      continueAsGuest, login, register, logout, saveProfile, toggleSaved, completeOnboarding, toast,
      recs, routine, recsError, topSlugs, refreshRecs, tryOn, setTryOn, scanPhoto, setScanPhoto, clearScanHistory, brandLogo, refreshBrand,
      analyticsOn, setAnalytics, rememberScan, setRememberScan, clearDeviceData, deleteAccount, applySession: finishAuth, applyUser, dataEpoch,
    }),
    [ready, mode, user, profile, saved, onboarded, hasProfile, scanDraft, continueAsGuest, login, register, logout, saveProfile, toggleSaved,
      completeOnboarding, toast, recs, routine, recsError, topSlugs, refreshRecs, tryOn, scanPhoto, clearScanHistory, brandLogo, refreshBrand,
      analyticsOn, setAnalytics, rememberScan, setRememberScan, clearDeviceData, deleteAccount, finishAuth, applyUser, dataEpoch],
  );

  return (
    <AppContext.Provider value={value}>
      {children}
      {toastMsg ? <Toast key={toastMsg.id} msg={toastMsg.msg} kind={toastMsg.kind} /> : null}
    </AppContext.Provider>
  );
}

function Toast({ msg, kind }) {
  const bg = kind === "error" ? "bg-[#EF4444]" : "bg-navy";
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-24 z-[1000] flex justify-center md:bottom-8" role="status" aria-live="polite">
      <div data-testid="app-toast" className={`${bg} animate-toast-in max-w-[520px] rounded-xl px-4 py-3 font-body-semi text-sm text-white shadow-xl`}>
        {msg}
      </div>
    </div>
  );
}
